import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCurrentUser } from '@/lib/auth-context';

import { useCreateRole } from './useRoles';
import type { RoleDto } from './types';

const schema = z.object({
  role_code: z
    .string()
    .min(2)
    .regex(/^[a-z][a-z0-9_]*$/, 'use lowercase letters, numbers, and underscores'),
  display_name: z.string().min(1),
  description: z.string().optional(),
  party_type: z.enum(['supplier', 'processor', 'platform']),
});

type RoleCreateValues = z.infer<typeof schema>;

interface RoleCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (role: RoleDto) => void;
}

export const RoleCreateDialog = ({ open, onOpenChange, onCreated }: RoleCreateDialogProps) => {
  const { t } = useTranslation('settings');
  const user = useCurrentUser();
  const create = useCreateRole();
  const canPickParty =
    user?.roles.includes('system_admin') || user?.roles.includes('platform_admin');

  const form = useForm<RoleCreateValues>({
    resolver: zodResolver(schema),
    defaultValues: { role_code: '', display_name: '', description: '', party_type: 'platform' },
  });

  useEffect(() => {
    if (!open) {
      form.reset({ role_code: '', display_name: '', description: '', party_type: 'platform' });
    }
  }, [form, open]);

  const submit = form.handleSubmit(async (values) => {
    const role = await create.mutateAsync({
      role_code: values.role_code.trim(),
      display_name: values.display_name.trim(),
      description: values.description?.trim() || undefined,
      party_type: canPickParty ? values.party_type : undefined,
      permissions: [],
    });
    onCreated?.(role);
    onOpenChange(false);
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('efs.settings.roles.actions.create')}</SheetTitle>
          <SheetDescription>{t('efs.settings.roles.create_hint')}</SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.roles.fields.display_name')}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.roles.fields.role_code')}</FormLabel>
                  <FormControl>
                    <Input placeholder="processor_site_staff" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {canPickParty ? (
              <FormField
                control={form.control}
                name="party_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.roles.fields.party_type')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(['platform', 'processor', 'supplier'] as const).map((party) => (
                          <SelectItem key={party} value={party}>
                            {t(`efs.settings.companies.types.${party}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.settings.roles.fields.description')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {create.error ? (
              <p className="text-sm text-destructive">{create.error.message}</p>
            ) : null}
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('efs.settings.roles.actions.create')}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
